import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  type AppliedPropertyFilter,
  GetOperationLabel,
  type GiftHistoryVal,
  OperationsWithoutVal,
  type PropertyFilterInfo,
} from 'donor-elf-web/lib/filter-utils-new';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import FormatAmountService from 'donor-elf-web/services/format-amount-service';
import FormatDateService from 'donor-elf-web/services/format-date-service';

interface ComponentArgs {
  appliedPropertyFilter: AppliedPropertyFilter;
  availablePropertyFiltersByPropertyName: {
    [propertyName: string]: PropertyFilterInfo;
  };
  onEdit: (appliedPropertyFilter: AppliedPropertyFilter) => void;
  onDelete: (appliedPropertyFilter: AppliedPropertyFilter) => void;
}

export default class FilterAppliedFilterComponent extends Component<ComponentArgs> {
  @service declare formatAmountService: FormatAmountService;
  @service declare formatDateService: FormatDateService;

  @cached
  get availablePropertyFilterItem() {
    return this.args.availablePropertyFiltersByPropertyName[
      this.args.appliedPropertyFilter.propertyName
    ];
  }

  get hasOperationPropertyType() {
    return this.availablePropertyFilterItem.propertyType !== 'no-operation';
  }

  get giftHistoryPropertyType() {
    return this.availablePropertyFilterItem.propertyType === 'giftHistory';
  }

  get distanceFromPropertyType() {
    return this.availablePropertyFilterItem.propertyType === 'distanceFrom';
  }

  get isContactIdsPropertyType() {
    return this.availablePropertyFilterItem.propertyType === 'contactIds';
  }

  get giftHistoryValTypeIsAmount() {
    return (
      (this.args.appliedPropertyFilter.val as GiftHistoryVal).valType ===
      'amount'
    );
  }

  get propertyLabel() {
    return this.availablePropertyFilterItem.label;
  }

  get operationLabel() {
    let { availablePropertyFilterItem } = this;
    return GetOperationLabel(this.args.appliedPropertyFilter.operation!, {
      propertyType: availablePropertyFilterItem.propertyType,
    });
  }

  get showVal() {
    return (
      OperationsWithoutVal[this.args.appliedPropertyFilter.operation!] !== true
    );
  }

  get encloseValInQuotes() {
    let propertyType = this.availablePropertyFilterItem.propertyType;
    return propertyType === 'string' || propertyType === 'list';
  }

  get listValMapping(): { [val: string]: string } {
    let mapping: any = {};
    for (let listOption of this.availablePropertyFilterItem.listOptions || []) {
      if (typeof listOption === 'string') {
        mapping[listOption] = listOption;
      } else {
        mapping[listOption.val] = listOption.label;
      }
    }
    return mapping;
  }

  get isDateBetweenPropertyTypeAndOperation() {
    return (
      this.availablePropertyFilterItem.propertyType === 'date' &&
      this.args.appliedPropertyFilter.operation === '><'
    );
  }

  get isEditable() {
    return !this.availablePropertyFilterItem.systemOnly;
  }

  get isListPropertyTypeWithVal() {
    return (
      this.availablePropertyFilterItem.propertyType === 'list' && this.showVal
    );
  }

  get isListPropertyTypeWithAnyOperationAndSingleVal() {
    return (
      this.availablePropertyFilterItem.propertyType === 'list' &&
      this.args.appliedPropertyFilter.operation === 'any' &&
      this.formattedListVals.length === 1
    );
  }

  get formattedVal(): any {
    if (OperationsWithoutVal[this.args.appliedPropertyFilter.operation!]) {
      return undefined;
    }
    let { availablePropertyFilterItem } = this;
    if (availablePropertyFilterItem.propertyType === 'date') {
      return this.formatDateService.format(
        this.args.appliedPropertyFilter.val as string,
      );
    } else if (availablePropertyFilterItem.propertyType === 'amount') {
      return this.formatAmountService.format(
        this.args.appliedPropertyFilter.val as string,
        { removeNoCents: true },
      );
    } else if (availablePropertyFilterItem.propertyType === 'boolean') {
      return this.args.appliedPropertyFilter.val!.toString() === 'true'
        ? 'Yes'
        : 'No';
    } else if (
      availablePropertyFilterItem.propertyType === 'string' &&
      (
        availablePropertyFilterItem.listOptions! as SelectOption[] | undefined
      )?.[0]?.val?.length > 0
    ) {
      return this.listValMapping[this.args.appliedPropertyFilter.val as string];
    }
    return this.args.appliedPropertyFilter.val;
  }

  get formattedListVals(): string[] {
    let valArray = this.args.appliedPropertyFilter.val as string[];
    let listOptions = this.availablePropertyFilterItem.listOptions as
      | SelectOption[]
      | string[];
    if (typeof listOptions[0] === 'string') {
      return (listOptions as string[]).filter((x) => valArray.includes(x));
    } else {
      return (listOptions as SelectOption[])
        .filter((x) => valArray.includes(x.val))
        .map((x) => x.label);
    }
  }

  get isEqualOperation() {
    return this.args.appliedPropertyFilter.operation === '=';
  }

  @action
  handleEdit() {
    this.args.onEdit(this.args.appliedPropertyFilter);
  }

  @action
  handleDelete() {
    this.args.onDelete(this.args.appliedPropertyFilter);
  }
}
